import { useEffect, useState } from 'react';
import { api } from '../App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { EnvelopeSimple, PaperPlaneTilt, Gift, CalendarCheck, ChatCircleDots, Paperclip, X } from '@phosphor-icons/react';
import { KATEGORIJE_DJELATNOSTI } from './ClientsPage';

export default function CestitkePage() {
  const [form, setForm] = useState({ subject: '', poruka: '', vjera: '', nacionalnost: '', kategorija_djelatnosti: '' });
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const [sendingViber, setSendingViber] = useState(false);
  const [logs, setLogs] = useState([]);
  const [birthdaysToday, setBirthdaysToday] = useState([]);
  const [sendingBirthday, setSendingBirthday] = useState(false);
  const [cfgVjere, setCfgVjere] = useState([]);
  const [cfgNacionalnosti, setCfgNacionalnosti] = useState([]);
  const [templates, setTemplates] = useState([]);

  useEffect(() => {
    fetchLogs();
    fetchBirthdays();
    fetchConfig();
    fetchTemplates();
  }, []);

  const fetchConfig = async () => {
    try { const r = await api.get('/settings/config-lists'); setCfgVjere(r.data.vjere || []); setCfgNacionalnosti(r.data.nacionalnosti || []); } catch {}
  };
  const fetchTemplates = async () => {
    try { const r = await api.get('/settings/templates'); setTemplates(r.data); } catch {}
  };
  const applyTemplate = (t) => {
    setForm(prev => ({ ...prev, subject: t.naslov || t.subject || '', poruka: t.poruka || t.sadrzaj || '' }));
  };

  const fetchLogs = async () => {
    try { const r = await api.get('/cestitke/log'); setLogs(r.data); } catch {}
  };
  const fetchBirthdays = async () => {
    try { const r = await api.get('/cestitke/birthday-check'); setBirthdaysToday(r.data); } catch {}
  };

  const handleSend = async (kanal = 'email', onlyFailed = false) => {
    if (!form.poruka.trim()) { toast.error('Unesite poruku'); return; }
    if (kanal === 'email' && !form.subject.trim()) { toast.error('Unesite naslov emaila'); return; }
    const setter = kanal === 'viber' ? setSendingViber : setSending;
    setter(true);
    try {
      let r;
      // If attachments are present AND channel is email, use multipart endpoint
      if (kanal === 'email' && attachments.length > 0) {
        const fd = new FormData();
        fd.append('subject', form.subject || '-');
        fd.append('poruka', form.poruka);
        if (form.vjera) fd.append('vjera', form.vjera);
        if (form.nacionalnost) fd.append('nacionalnost', form.nacionalnost);
        if (form.kategorija_djelatnosti) fd.append('kategorija_djelatnosti', form.kategorija_djelatnosti);
        fd.append('kanal', kanal);
        fd.append('only_failed', String(onlyFailed));
        attachments.forEach(f => fd.append('files', f));
        r = await api.post('/cestitke/send-with-files', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      } else {
        const payload = { ...form, kanal, only_failed: onlyFailed };
        if (!payload.vjera) delete payload.vjera;
        if (!payload.nacionalnost) delete payload.nacionalnost;
        if (!payload.kategorija_djelatnosti) delete payload.kategorija_djelatnosti;
        if (kanal === 'viber' && !payload.subject) payload.subject = '-';
        r = await api.post('/cestitke/send', payload);
      }
      const d = r.data;
      if (d.started) {
        toast.success(
          `📨 ${kanal === 'viber' ? 'Viber' : 'Email'} čestitka pokrenuta u pozadini! ${d.eligible} primalaca, ETA ~${d.eta_minutes} min.` +
          (onlyFailed && d.already_done > 0 ? ` Preskočeno ${d.already_done} (već uspješno primili).` : ''),
          { duration: 12000 }
        );
      } else {
        toast.info(d.message || 'Nema klijenata za slanje', { duration: 8000 });
      }
      if (!onlyFailed) { setForm({ subject: '', poruka: '', vjera: '', nacionalnost: '', kategorija_djelatnosti: '' }); setAttachments([]); }
      fetchLogs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Greška pri slanju');
    } finally { setter(false); }
  };

  const handleSendBirthdays = async () => {
    setSendingBirthday(true);
    try {
      const r = await api.post('/cestitke/send-birthday');
      if (r.data.sent > 0) toast.success(`Poslano ${r.data.sent} rođendanskih čestitki!`);
      else toast.info('Nema novih rođendana za danas ili su već poslane');
      fetchBirthdays();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Greška');
    } finally { setSendingBirthday(false); }
  };

  const formatDT = (dt) => {
    if (!dt) return '-';
    try {
      const d = new Date(dt);
      const dd = String(d.getDate()).padStart(2, '0');
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const hh = String(d.getHours()).padStart(2, '0');
      const min = String(d.getMinutes()).padStart(2, '0');
      return `${dd}.${mm}.${d.getFullYear()} ${hh}:${min}`;
    } catch { return dt; }
  };

  return (
    <div className="space-y-6" data-testid="cestitke-page">
      <div>
        <h3 className="text-lg font-medium text-slate-900">Čestitke klijentima</h3>
        <p className="text-sm text-slate-600 mt-1">Pošaljite čestitke za praznike i provjerite rođendane</p>
      </div>

      {/* Birthday check */}
      <Card className="p-5 border-purple-200 bg-purple-50/30" data-testid="birthday-card">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Gift size={20} className="text-purple-600" />
            <h4 className="text-sm font-semibold text-purple-800">Rođendani danas</h4>
          </div>
          <Button onClick={handleSendBirthdays} disabled={sendingBirthday || birthdaysToday.length === 0}
            className="bg-purple-600 hover:bg-purple-700 text-white text-sm" data-testid="send-birthday-btn">
            <Gift size={16} className="mr-2" /> {sendingBirthday ? 'Šaljem...' : 'Pošalji čestitke'}
          </Button>
        </div>
        {birthdaysToday.length === 0 ? (
          <p className="text-sm text-slate-500">Nema rođendana danas</p>
        ) : (
          <div className="space-y-2">
            {birthdaysToday.map((c, i) => (
              <div key={i} className="flex items-center gap-3 p-2 bg-white rounded border border-purple-100">
                <Gift size={16} className="text-purple-500" />
                <span className="text-sm font-medium">{c.direktor || c.skraceni_naziv}</span>
                <span className="text-xs text-slate-500">({c.skraceni_naziv || c.naziv_firme})</span>
                <span className="text-xs text-slate-400 ml-auto">{c.email || 'nema email'}</span>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Templates */}
      {templates.length > 0 && (
        <Card className="p-5" data-testid="templates-list">
          <h4 className="text-sm font-semibold text-slate-800 mb-3">Dostupni šabloni poruka</h4>
          <div className="divide-y divide-slate-100">
            {templates.map(t => (
              <div key={t.id} className="py-3 flex justify-between items-start">
                <div className="flex-1">
                  <h5 className="text-sm font-bold text-slate-700">{t.naziv}</h5>
                  <p className="text-xs text-slate-500">Naslov: {t.naslov || t.subject || '-'}</p>
                  <p className="text-sm text-slate-600 mt-1">{t.poruka || t.sadrzaj || ''}</p>
                  <div className="flex gap-2 mt-1">
                    <span className="px-2 py-0.5 rounded text-xs bg-slate-100 text-slate-600">{t.kanal || t.tip || 'email'}</span>
                    <span className="px-2 py-0.5 rounded text-xs bg-slate-100 text-slate-600">{t.jezik || 'sr'}</span>
                  </div>
                </div>
                <Button onClick={() => applyTemplate(t)} variant="outline" size="sm" className="ml-3 shrink-0 text-xs" data-testid={`use-template-${t.id}`}>
                  Koristi
                </Button>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Send greeting */}
      <Card className="p-5" data-testid="cestitka-form">
        <div className="flex items-center gap-2 mb-4">
          <EnvelopeSimple size={20} className="text-blue-600" />
          <h4 className="text-sm font-semibold text-slate-800">Pošalji čestitku</h4>
        </div>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Filter po vjeri (opciono)</Label>
              <Select value={form.vjera || 'all'} onValueChange={v => setForm({...form, vjera: v === 'all' ? '' : v})}>
                <SelectTrigger className="bg-white border-slate-300 text-sm" data-testid="cestitka-vjera-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Svi klijenti</SelectItem>
                  {cfgVjere.map(v => (
                    <SelectItem key={v} value={v.toLowerCase()}>{v.charAt(0).toUpperCase() + v.slice(1)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Filter po nacionalnosti (opciono)</Label>
              <Select value={form.nacionalnost || 'all'} onValueChange={v => setForm({...form, nacionalnost: v === 'all' ? '' : v})}>
                <SelectTrigger className="bg-white border-slate-300 text-sm" data-testid="cestitka-nac-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Sve nacionalnosti</SelectItem>
                  {cfgNacionalnosti.map(n => (
                    <SelectItem key={n} value={n.toLowerCase()}>{n.charAt(0).toUpperCase() + n.slice(1)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-slate-500">Filter po kategoriji djelatnosti (opciono)</Label>
            <Select
              value={form.kategorija_djelatnosti || 'all'}
              onValueChange={v => setForm({...form, kategorija_djelatnosti: v === 'all' ? '' : v})}
            >
              <SelectTrigger className="bg-white border-slate-300 text-sm" data-testid="cestitka-kategorija-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Sve kategorije</SelectItem>
                {KATEGORIJE_DJELATNOSTI.map(k => (
                  <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500 mt-1">Pošalji obavještenje samo klijentima iz odabrane djelatnosti (npr. samo trgovcima ili samo ugostiteljima).</p>
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-slate-500">Naslov emaila *</Label>
            <Input value={form.subject} onChange={e => setForm({...form, subject: e.target.value})}
              placeholder="npr: Srećan Vaskrs! / Gëzuar Pashkët!" className="bg-white border-slate-300 text-sm" data-testid="cestitka-subject" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-slate-500">Poruka *</Label>
            <textarea value={form.poruka} onChange={e => setForm({...form, poruka: e.target.value})}
              placeholder="Napišite poruku na jeziku koji odgovara klijentima..."
              className="flex w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm min-h-[120px] resize-y focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="cestitka-poruka" />
          </div>

          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <Label className="text-xs text-slate-500 flex items-center gap-1.5">
                <Paperclip size={13} weight="bold" />
                Prilozi (Email — bilo koji tip fajla, ukupno max 15 MB)
              </Label>
              {attachments.length > 0 && (
                <span className="text-xs text-slate-500">
                  {attachments.length} fajl(a) • {(attachments.reduce((s, f) => s + f.size, 0) / (1024 * 1024)).toFixed(2)} MB
                </span>
              )}
            </div>
            <label
              htmlFor="cestitka-file-input"
              className="flex items-center justify-center gap-2 border-2 border-dashed border-slate-300 rounded-md px-3 py-3 cursor-pointer hover:bg-slate-50 transition"
              data-testid="cestitka-attach-dropzone"
            >
              <Paperclip size={16} className="text-slate-500" />
              <span className="text-sm text-slate-600">Kliknite da dodate više fajlova (PDF, Word, Excel, slike, bilo šta)</span>
            </label>
            <input
              id="cestitka-file-input"
              type="file"
              multiple
              className="hidden"
              onChange={(e) => {
                const list = Array.from(e.target.files || []);
                const total = [...attachments, ...list].reduce((s, f) => s + f.size, 0);
                if (total > 15 * 1024 * 1024) {
                  toast.error('Ukupna veličina priloga prelazi 15 MB. Smanjite fajlove.');
                  e.target.value = '';
                  return;
                }
                setAttachments(prev => [...prev, ...list]);
                e.target.value = '';
              }}
              data-testid="cestitka-file-input"
            />
            {attachments.length > 0 && (
              <div className="mt-2 space-y-1" data-testid="cestitka-attachments-list">
                {attachments.map((f, i) => (
                  <div key={i} className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded px-3 py-1.5 text-sm">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <Paperclip size={13} className="text-slate-400 shrink-0" />
                      <span className="truncate">{f.name}</span>
                      <span className="text-xs text-slate-400 shrink-0">{(f.size / 1024).toFixed(1)} KB</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setAttachments(prev => prev.filter((_, idx) => idx !== i))}
                      className="p-1 hover:bg-red-50 rounded text-red-500 shrink-0"
                      data-testid={`cestitka-remove-file-${i}`}
                      title="Ukloni fajl"
                    >
                      <X size={13} weight="bold" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <p className="text-xs text-slate-500 mt-1">Prilozi se šalju samo preko Email kanala. Viber ignoriše priloge.</p>
          </div>
          <div className="flex gap-3 flex-wrap">
            <Button onClick={() => handleSend('email', false)} disabled={sending || sendingViber} className="bg-green-600 hover:bg-green-700 text-white" data-testid="cestitka-send-email-btn">
              <EnvelopeSimple size={16} className="mr-2" /> {sending ? 'Šaljem...' : 'Pošalji Email (svima)'}
            </Button>
            <Button onClick={() => handleSend('email', true)} disabled={sending || sendingViber} variant="outline" className="border-amber-400 text-amber-700 hover:bg-amber-50" data-testid="cestitka-send-email-failed-btn">
              <EnvelopeSimple size={16} className="mr-2" /> Pošalji samo neuspjele
            </Button>
            <Button onClick={() => handleSend('viber', false)} disabled={sending || sendingViber} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="cestitka-send-viber-btn">
              <ChatCircleDots size={16} className="mr-2" /> {sendingViber ? 'Šaljem...' : 'Pošalji Viber'}
            </Button>
          </div>
        </div>
      </Card>

      {/* Log */}
      {logs.length > 0 && (
        <Card className="overflow-hidden" data-testid="cestitke-log">
          <div className="p-4 border-b bg-slate-50">
            <h4 className="text-sm font-semibold text-slate-700">Istorija poslanih čestitki</h4>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm text-slate-600">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Datum</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Naslov</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Vjera</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Nacionalnost</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Kanal</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Poslano</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Poslao</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((l, i) => (
                  <tr key={i} className="border-b hover:bg-slate-50">
                    <td className="px-4 py-3 text-xs">{formatDT(l.sent_at)}</td>
                    <td className="px-4 py-3 font-medium max-w-xs truncate">{l.subject}</td>
                    <td className="px-4 py-3 text-xs">{l.vjera || 'Svi'}</td>
                    <td className="px-4 py-3 text-xs">{l.nacionalnost || 'Sve'}</td>
                    <td className="px-4 py-3">
                      {l.kanal === 'viber' ? (
                        <span className="px-2 py-0.5 rounded text-xs font-medium bg-purple-50 text-purple-700 border border-purple-200">Viber</span>
                      ) : (
                        <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">Email</span>
                      )}
                    </td>
                    <td className="px-4 py-3"><span className="px-2 py-1 rounded text-xs font-medium bg-green-50 text-green-700 border border-green-200">{l.sent_count} ok / {l.failed_count} fail</span></td>
                    <td className="px-4 py-3 text-xs">{l.sent_by}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
